<script setup lang="ts">
  import { ref, onMounted, watch, onUnmounted, defineAsyncComponent } from 'vue'
  import { useCommonStore, useTravelersStore, useUserStore } from '@/shared/store'
  import type { TravelerInformation as ApiTravelerInformation } from '@/shared/types/api.types'
  import { HOLDER_ID } from '@/widgets/Flow/FlowInsuredTravelersWidgetQC/FlowInsuredTravelersWidgetQC.constants'
  import {
    useApiRequestsAbort,
    useWinterSport,
    useGoogleAnalytics,
    usePerformanceTiming,
  } from '@/shared/composables'
  import { TravelDirection } from '@/shared/types/common.types'
  import { AnalyticsPageName } from '@/shared/constants/analytics.constants'
  import { useFormDataWithRedirect } from '@/shared/composables/useFormDataWithRedirect'
  import { FlowInsuredTravelersWidgetQC } from '@/widgets/Flow'

  withDefaults(defineProps<Props>(), {
    withBackButton: false,
  })
  const emit = defineEmits<Emits>()

  const FlowPersonalInformationWidgetQC = defineAsyncComponent(
    () =>
      import('@/widgets/Flow/FlowPersonalInformationWidgetQC/FlowPersonalInformationWidgetQC.vue')
  )

  const FlowTravelerFormWidgetQC = defineAsyncComponent(
    () => import('@/widgets/Flow/FlowTravelerFormWidgetQC/FlowTravelerFormWidgetQC.vue')
  )

  interface Props {
    withBackButton?: boolean
  }

  interface Emits {
    (e: 'proceed'): void
    (e: 'load', request: () => Promise<any>, requestId?: string, withLoader?: boolean): void
    (e: 'back'): void
  }
  useApiRequestsAbort()

  const { getFormData } = useFormDataWithRedirect()
  const travelersStore = useTravelersStore()
  const commonStore = useCommonStore()
  const userStore = useUserStore()
  const { withWinterSport } = useWinterSport()
  const gaService = useGoogleAnalytics()
  const { onPageReady, onPerformanceCheckEnd } = usePerformanceTiming()

  const isTravelerFormActive = ref(false)
  const isHolderFormActive = ref(false)
  const travelerInformation = ref<ApiTravelerInformation | null>(null)
  const travelersInformation = ref<ApiTravelerInformation[]>([])
  const winterSportPrice = ref(0)
  const loadingTravelerForm = ref(false)

  const onProceed = () => {
    emit('proceed')
  }

  const fetchTravellers = async () => {
    travelersInformation.value = await travelersStore.getTravelers()
  }

  const onDelete = async (id: string) => {
    await emit('load', async () => {
      await travelersStore.deleteTraveler(id)
      await fetchTravellers()
    })
  }

  const onEdit = (id: string) => {
    userStore.setActiveInsuredTravelersSubPage('edit')
    gaService.sendViewPage(TravelDirection.OUTBOUND, AnalyticsPageName.UPDATE_INFORMATION)
    if (id === HOLDER_ID) {
      isHolderFormActive.value = true
      return
    }
    const possibleTravelerToEdit = travelersInformation.value.find(
      (traveler) => String(traveler.srNo) === id
    )
    if (!possibleTravelerToEdit) return new Error('no traveler to edit')
    travelerInformation.value = possibleTravelerToEdit
    isTravelerFormActive.value = true
    fetchRelations()
  }

  const onAdd = () => {
    userStore.setActiveInsuredTravelersSubPage('add')
    gaService.sendViewPage(TravelDirection.OUTBOUND, AnalyticsPageName.ADD_PERSON)
    travelerInformation.value = null
    isTravelerFormActive.value = true
    fetchRelations()
  }

  const saveTraveler = async (traveler: ApiTravelerInformation) => {
    await emit(
      'load',
      async () => {
        loadingTravelerForm.value = true
        await travelersStore.saveTraveler(traveler)
        await fetchTravellers()
        loadingTravelerForm.value = false
        isTravelerFormActive.value = false
        isHolderFormActive.value = false

        if (userStore.activeInsuredTravelersSubPage === 'add') {
          gaService.sendInsuredTravelersAddNew(
            TravelDirection.OUTBOUND,
            traveler.relationName,
            traveler.nationalityName
          )
        } else {
          gaService.sendInsuredTravelersUpdateProceed(
            TravelDirection.OUTBOUND,
            traveler.nationalityName,
            traveler.relationName
          )
        }
      },
      'saveTraveler',
      false
    )
  }

  const fetchNationalities = async () => {
    if (commonStore.nationalities.length > 0) return

    const data = await commonStore.findNationalities()
    commonStore.setNationalities(data)
  }

  const fetchRelations = async () => {
    if (commonStore.relations.length > 0) return

    const data = await commonStore.getRelations()
    commonStore.setRelations(data)
  }

  onMounted(() => {
    emit('load', async () => {
      onPageReady()

      if (!(await getFormData(TravelDirection.OUTBOUND, true))) return
      await Promise.all([fetchTravellers(), fetchNationalities()])

      onPerformanceCheckEnd()
      gaService.sendViewPage(TravelDirection.OUTBOUND, AnalyticsPageName.INSURED_PEOPLE)
    })
  })

  // TODO: Refactor the way of handling go back
  watch(
    () => isTravelerFormActive.value,
    (newValue) => {
      commonStore.setStopBack(newValue)

      if (!newValue) {
        commonStore.setBackCallback(null)
        userStore.setActiveInsuredTravelersSubPage(undefined)
      } else {
        commonStore.setBackCallback(() => (isTravelerFormActive.value = false))
      }
    }
  )

  watch(
    () => isHolderFormActive.value,
    (newValue) => {
      commonStore.setStopBack(newValue)

      if (!newValue) {
        commonStore.setBackCallback(null)
        userStore.setActiveInsuredTravelersSubPage(undefined)
      } else {
        commonStore.setBackCallback(() => (isHolderFormActive.value = false))
      }
    }
  )

  onUnmounted(() => {
    commonStore.setStopBack(false)
    commonStore.setBackCallback(null)
    userStore.setActiveInsuredTravelersSubPage(undefined)
  })
</script>

<template>
  <FlowTravelerFormWidgetQC
    v-if="isTravelerFormActive"
    :information="travelerInformation"
    :with-winter-sport="withWinterSport"
    :is-loading="loadingTravelerForm"
    :winter-sport-price="winterSportPrice"
    :with-back-button="withBackButton"
    @save="saveTraveler"
    @back="emit('back')"
  />

  <FlowPersonalInformationWidgetQC
    v-else-if="isHolderFormActive"
    :with-winter-sport="false"
    :winter-sport-price="winterSportPrice"
    is-edit
    :with-back-button="withBackButton"
    @load="emit('load', $event, 'personal-information', false)"
    @proceed="isHolderFormActive = false"
    @back="emit('back')"
  />

  <FlowInsuredTravelersWidgetQC
    v-else
    :travelers="travelersInformation"
    :with-back-button="withBackButton"
    @add="onAdd"
    @edit="onEdit"
    @delete="onDelete"
    @proceed="onProceed"
    @back="emit('back')"
  />
</template>
